/**
 * @file useUserCenterPage.js
 * @description 个人中心页逻辑（桌面端 + 移动端布局、子模块切换、签到）
 * @module features/user/composables/useUserCenterPage
 */

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { goHomeAfterLogout } from '@/shared/utils/logoutNavigation'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { fetchClockRecordApi, signOutApi } from '@/features/user/api/userApi'
import { clearAuthSession, getAuthToken } from '@/shared/utils/auth'
import { openGoldRecharge } from '@/features/user/composables/useGoldRecharge'
import {
  DEFAULT_USER_CENTER_MODULE,
  USER_CENTER_HIDDEN_MODULES,
  USER_CENTER_MODULE_COMPONENTS,
  USER_CENTER_VALID_MODULES,
  resolveVisibleUserCenterModule,
} from '@/features/user/constants/userCenterModules'

/**
 * @returns {Object} 个人中心页模板绑定数据与方法
 */
export function useUserCenterPage() {
  const route = useRoute()
  const router = useRouter()
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)

  userStore.fetchUserInfo()

  const isMobile = ref(false)
  const activeModuleKey = ref(DEFAULT_USER_CENTER_MODULE)
  const activeSubMenu = ref(DEFAULT_USER_CENTER_MODULE)
  const showTooltipMessage = ref(false)
  const checkinCompleted = ref(false)
  const showHoverTooltip = ref(false)
  const showCheckinSuccess = ref(false)
  const showCalendar = ref(false)
  const coins = ref(2)
  const hoveredItem = ref(null)
  const showMembershipCenter = ref(false)

  const membershipItems = [
    { name: 'Balance', label: '我的余额', icon: 'Wallet' },
    { name: 'Membership', label: '我的会员', icon: 'Medal' },
  ]

  const accountItems = [
    { name: 'UserBaseInfo', label: '基本资料', icon: 'User' },
    { name: 'UserFavorites', label: '我的收藏', icon: 'Star' },
    { name: 'UserHistory', label: '观看历史', icon: 'Clock' },
    { name: 'UserFeedback', label: '我的反馈', icon: 'ChatDotRound' },
    // 手机号登录阶段暂隐：{ name: 'UserPasswordSetting', label: '密码设置', icon: 'Key' },
  ]

  const quickActions = [
    { name: 'UserFavorites', label: '我的收藏', icon: 'Star' },
    { name: 'UserHistory', label: '观看历史', icon: 'Clock' },
    { name: 'UserFeedback', label: '我的反馈', icon: 'ChatDotRound' },
    // 手机号登录阶段暂隐：{ name: 'UserPasswordSetting', label: '密码设置', icon: 'Key' },
  ]

  const goldAmount = computed(() => user.value?.userGoldInfo?.goldAmount ?? 0)
  const totalConsumeAmount = computed(
    () => user.value?.userGoldInfo?.totalConsumeAmount ?? 0
  )

  const activeComponent = computed(
    () => USER_CENTER_MODULE_COMPONENTS[activeModuleKey.value] || null
  )

  function updateIsMobile() {
    isMobile.value = isMobileLayoutViewport()
  }

  /**
   * 加载当月签到记录，更新 store 连续签到天数
   */
  async function loadClockRecordForContinuousDays() {
    try {
      const token =
        typeof localStorage !== 'undefined'
          ? JSON.parse(localStorage.getItem('user') || 'null')?.token || ''
          : ''
      if (!token) return

      const now = new Date()
      const response = await fetchClockRecordApi({
        clockYear: now.getFullYear(),
        clockMonth: now.getMonth() + 1,
      })

      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        if (data.continuousDays !== undefined && data.continuousDays !== null) {
          userStore.setContinuousDays(data.continuousDays)
        }
      }
    } catch (error) {
      console.warn('加载签到记录失败:', error?.message)
    }
  }

  function updateActiveComponent(module) {
    const next = resolveVisibleUserCenterModule(module)
    if (USER_CENTER_VALID_MODULES.includes(next)) {
      activeModuleKey.value = next
      activeSubMenu.value = next
    } else {
      console.warn(`组件 ${module} 不存在`)
    }
  }

  /**
   * 打开个人中心子模块。金币记录可带 recordType 定位到对应栏。
   * @param {string} module
   * @param {{ recordType?: string }} [extraQuery]
   */
  async function goToModule(module, extraQuery = {}) {
    if (
      !USER_CENTER_VALID_MODULES.includes(module) ||
      USER_CENTER_HIDDEN_MODULES.has(module)
    ) {
      console.warn(`组件 ${module} 不存在或已隐藏`)
      return
    }
    const query = extraQuery.recordType
      ? { recordType: extraQuery.recordType }
      : {}
    if (isMobile.value) {
      await router.push({
        name: 'UserModule',
        params: { module },
        query,
      })
      return
    }
    const nextQuery = { ...route.query, module }
    if (extraQuery.recordType) {
      nextQuery.recordType = extraQuery.recordType
    } else {
      delete nextQuery.recordType
    }
    await router.replace({ query: nextQuery })
    activeModuleKey.value = module
    activeSubMenu.value = module
  }

  function navigateTo(module) {
    goToModule(module)
  }

  /**
   * 点当前余额打开橘子币充值窗（不进隐藏的余额充值中心，也不进金币记录）
   */
  function openGoldBalance() {
    openGoldRecharge({ source: 'user-center' })
  }

  function showTooltip() {
    showTooltipMessage.value = true
  }

  function hideTooltip() {
    showTooltipMessage.value = false
  }

  function handleDailyCheckin() {
    if (checkinCompleted.value) return
    coins.value += 2
    checkinCompleted.value = true
    showCheckinSuccess.value = true
  }

  function closeCheckinSuccess() {
    showCheckinSuccess.value = false
  }

  function showCheckinCalendar() {
    showCalendar.value = true
  }

  function closeCheckinCalendar() {
    showCalendar.value = false
  }

  async function handleCheckinSuccess() {
    await userStore.fetchUserInfo()
  }

  /** 退出登录（NavBar 等入口可复用） */
  async function handleLogout(emit) {
    try {
      const token = getAuthToken()
      if (!token) {
        emit?.('logout')
        goHomeAfterLogout(router)
        return
      }

      const response = await signOutApi()

      if (response.data.code === 200 || response.data.code === 0) {
        userStore.clearUser()
        clearAuthSession()
        goHomeAfterLogout(router)
      } else {
        console.error('退出失败:', response.data.msg || '未知错误')
      }
    } catch (error) {
      console.error('退出请求失败:', error.response?.data || error.message)
    }
  }

  watch(
    () => route.query.module,
    (newModule) => {
      const next = resolveVisibleUserCenterModule(
        typeof newModule === 'string' ? newModule : ''
      )
      updateActiveComponent(next)
    },
    { immediate: true }
  )

  onMounted(() => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    loadClockRecordForContinuousDays()
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateIsMobile)
  })

  return {
    user,
    isMobile,
    activeComponent,
    activeSubMenu,
    showTooltipMessage,
    checkinCompleted,
    showHoverTooltip,
    showCheckinSuccess,
    showCalendar,
    coins,
    hoveredItem,
    showMembershipCenter,
    membershipItems,
    accountItems,
    quickActions,
    goldAmount,
    totalConsumeAmount,
    navigateTo,
    goToModule,
    openGoldBalance,
    showTooltip,
    hideTooltip,
    handleDailyCheckin,
    closeCheckinSuccess,
    showCheckinCalendar,
    closeCheckinCalendar,
    handleCheckinSuccess,
    handleLogout,
  }
}
